/**
 * waypoint_hud — which waypoint you are following, and where it is from where you look (2026-09-24). Pure: no DOM,
 * no three; the storage is handed in.
 *
 * "An indicator showing waypoints in the world and what direction the waypoint is at, for when you set a waypoint on
 * the map." The HUD follows ONE waypoint (the TRACKED one) with a heading strip and, when it is in front of you, a
 * marker standing on it in the world; off screen or behind you, the marker rides the screen edge on the side you
 * should turn to.
 *
 *   THE TRACK LAW: a stored choice per world (`atlas:waypoint-track:<worldId>`: an id, or 'off'); no choice → the
 *   NEWEST waypoint you own here; a waypoint that appears after the HUD first saw your list is tracked at once — you
 *   just set it on a map, so that is the one you want to follow. A stored id that no longer exists falls back to the
 *   newest. 'off' holds until you track one again (a new waypoint turns it back on: you asked for it).
 *   THE BEARING: headings are compass angles — 0 = north = −Z (the maps draw −Z up), clockwise positive; the relative
 *   bearing (−π, π] is the waypoint's heading minus the camera's, so +90° means "on your right".
 *   THE EDGE: an off-screen target is placed where the ray from the screen centre toward it (in CAMERA space, never a
 *   projection — one behind the eye is mirrored) leaves an inset rectangle; behind you, the ray keeps the side of the
 *   SHORTER turn and drops below the horizon, never onto the middle of the top edge (which reads as "ahead").
 */

export interface TrackRow { waypointId: bigint; name: string; posX: number; posZ: number }

/** Within this many metres you have arrived (the HUD says so once). */
export const ARRIVE_M = 12;
/** Leaving by this many metres re-arms the arrival notice. */
export const ARRIVE_RESET_M = 30;

export function trackKey(worldId: bigint | string): string { return `atlas:waypoint-track:${String(worldId)}`; }

/**
 * The waypoint to follow. `stored` = the saved choice ('' none, 'off', or an id); `seenMax` = the largest id the HUD
 * had already seen before this call (null on the first look); returns the row (or null) and the choice to save.
 */
export function resolveTracked<T extends TrackRow>(rows: readonly T[], stored: string, seenMax: bigint | null): { row: T | null; save: string } {
  if (rows.length === 0) return { row: null, save: stored };
  let newest = rows[0];
  for (const r of rows) if (r.waypointId > newest.waypointId) newest = r;
  // A waypoint made since the last look: you just set it — follow it (and turn the HUD back on if it was off).
  if (seenMax !== null && newest.waypointId > seenMax) return { row: newest, save: String(newest.waypointId) };
  if (stored === 'off') return { row: null, save: 'off' };
  if (stored !== '') {
    const hit = rows.find((r) => String(r.waypointId) === stored);
    if (hit) return { row: hit, save: stored };
  }
  return { row: newest, save: stored === '' ? '' : String(newest.waypointId) };
}

/** Wrap an angle into (−π, π] (closed form: never loops on a hostile value). */
export function wrapPi(a: number): number {
  if (!Number.isFinite(a)) return 0;
  const t = 2 * Math.PI;
  let r = a - t * Math.floor((a + Math.PI) / t);
  if (r <= -Math.PI) r += t;
  return r;
}

/** Compass heading of a direction on the ground (0 = north = −Z, clockwise +). */
export function headingOf(dx: number, dz: number): number { return Math.atan2(dx, -dz); }

/** The waypoint's bearing relative to where the camera looks: (−π, π], + = to the right. */
export function relativeBearing(camHeading: number, fromX: number, fromZ: number, toX: number, toZ: number): number {
  return wrapPi(headingOf(toX - fromX, toZ - fromZ) - camHeading);
}

/** "ahead" / "ahead right" / "right" / "behind right" / "behind" … from a relative bearing. */
export function turnWords(rel: number): string {
  const d = (rel * 180) / Math.PI;
  const a = Math.abs(d);
  if (a <= 12) return 'ahead';
  const side = d > 0 ? 'right' : 'left';
  if (a <= 60) return `ahead ${side}`;
  if (a <= 120) return side;
  if (a < 168) return `behind ${side}`;
  return 'behind';
}

/**
 * Where an off-screen (or behind) marker sits: the ray from the screen centre along (dx, dy) leaves the rectangle
 * inset by `margin`. (dx, dy) is the target in CAMERA space read as screen axes — x = camera right, y = camera DOWN
 * (−y up) — never a projected point (a projection behind the eye is mirrored). `behind` keeps the side (x's sign is
 * the shorter turn) and pushes the ray below the horizon, so a target behind you never reads as "ahead" on the top
 * edge. Returns the point and the ray's angle (radians, screen space, 0 = right, +π/2 = down) for the arrow.
 */
export function edgePoint(W: number, H: number, margin: number, dx: number, dy: number, behind: boolean): { x: number; y: number; angle: number } {
  let x = dx, y = dy;
  if (behind) {
    if (Math.abs(x) < 1e-6) x = 1e-3; // dead astern: turn right
    y = Math.abs(y) * 0.35 + 0.2 * Math.abs(x); // below the horizon line, toward the bottom corners
  }
  if (Math.abs(x) < 1e-9 && Math.abs(y) < 1e-9) y = -1;
  const hw = Math.max(1, W / 2 - margin), hh = Math.max(1, H / 2 - margin);
  const k = Math.min(Math.abs(x) > 1e-9 ? hw / Math.abs(x) : Infinity, Math.abs(y) > 1e-9 ? hh / Math.abs(y) : Infinity);
  return { x: W / 2 + x * k, y: H / 2 + y * k, angle: Math.atan2(y, x) };
}

/** Arrival with hysteresis: arrive inside ARRIVE_M, re-arm only past ARRIVE_RESET_M. */
export function arrivalStep(dist: number, arrived: boolean): { arrived: boolean; justArrived: boolean } {
  if (!arrived && dist <= ARRIVE_M) return { arrived: true, justArrived: true };
  if (arrived && dist > ARRIVE_RESET_M) return { arrived: false, justArrived: false };
  return { arrived, justArrived: false };
}
